import React, { useCallback, useEffect, useRef, useState } from 'react';
import useEffectOnce from '../../hooks/useEffectOnce';
import { ChevronLeftIcon } from '../SvgIconsV3/ChevronLeft';
import { ChevronRightIcon } from '../SvgIconsV3/ChevronRight';
import {
  TilesWrapper,
  ScrollContainer,
  LeftButton,
  RightButton,
  hidden,
} from './TileScroller.styles';

export type TileScrollerProps = Omit<React.HTMLAttributes<HTMLElement>, 'onClick'> & {
  /**
   * Scrolls the tiles ot the center on load.  Note: This should not be used when
   * the tiles are above the fold.  The scroll will be visible to the user
   */
  scrollCenter?: boolean;
};

// The space between each tile, defined in TilesWrapper, default SPACING.SM
const getGap = (element: HTMLDivElement) =>
  parseInt(window.getComputedStyle(element).getPropertyValue('column-gap'), 10);

export const TileScroller: React.FC<TileScrollerProps> = (props) => {
  const { children, scrollCenter, ...rest } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scrollable, setScrollable] = useState(false);
  const [hideLeftButton, setHideLeftButton] = useState(true);
  const [hideRightButton, setHideRightButton] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const isScrollable = containerRef.current.scrollWidth > containerRef.current.clientWidth;
        setScrollable(isScrollable);
        setHideRightButton(!isScrollable);
      }
    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [containerRef]);

  const scrollTo = useCallback((position: 'left' | 'right') => {
    if (!containerRef.current) return;

    const isLeft = position === 'left';
    const isRight = position === 'right';
    const gap = getGap(containerRef.current);

    let scrollLeft = 0;
    const containerWidth = containerRef.current.clientWidth;
    const currentScrollLeft = containerRef.current.scrollLeft;

    for (const child of Array.from(containerRef.current.children)) {
      if (isLeft && scrollLeft >= currentScrollLeft - containerWidth) break;
      if (isRight && scrollLeft + child.clientWidth >= containerWidth + currentScrollLeft) break;

      scrollLeft += child.clientWidth + gap;
    }

    containerRef.current.scroll({
      left: scrollLeft,
    });
  }, []);

  const onScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { clientWidth, scrollLeft, scrollWidth } = event.currentTarget;
    setHideLeftButton(scrollLeft === 0);
    setHideRightButton(scrollLeft + clientWidth === scrollWidth);
  }, []);

  useEffectOnce(() => {
    if (scrollCenter) {
      if (containerRef.current) {
        containerRef.current.scrollLeft =
          containerRef.current.scrollWidth / 2 - containerRef.current.clientWidth / 2;
      }
    }
  });

  return (
    <ScrollContainer scrollable={scrollable} {...rest}>
      <LeftButton css={hideLeftButton && hidden} onClick={() => scrollTo('left')}>
        <ChevronLeftIcon width={20} height={20} />
      </LeftButton>
      <TilesWrapper onScroll={onScroll} ref={containerRef}>
        {children}
      </TilesWrapper>
      <RightButton css={hideRightButton && hidden} onClick={() => scrollTo('right')}>
        <ChevronRightIcon width={20} height={20} />
      </RightButton>
    </ScrollContainer>
  );
};
